/**
 * The ONE matcher — shared by replacement, scanning and scrubbing (SK-3).
 *
 * ## The defect this exists for
 *
 * Three parts of the skin pipeline decided "does this text contain this term",
 * and all three decided it differently:
 *
 * | | `makeTextPairs` + `replaceText` | `buildTermCatalog` + `countTermOccurrences` | `scrubHtmlSurface` |
 * |---|---|---|---|
 * | case | **sensitive** | insensitive | insensitive |
 * | surface | decoded text nodes | raw serialized html **and css** | parsed dom |
 * | word boundary | none | digit terms only | none |
 * | generic terms | not filtered | `GENERIC_SOURCE_TERMS` filtered | n/a |
 *
 * Every one of those disagreements is a way to produce a wrong answer that
 * another component then certifies as right. `"About"` was substituted
 * everywhere by the replacer AND rejected as a contamination term by the
 * scanner, so the same build both mangled the copy and reported it clean.
 * `Abshillbillygyros` (capital A, from the profile) never matched the 233
 * lowercase occurrences in the markup.
 *
 * ## Guarantees
 *
 * - **All matches are computed against the ORIGINAL string.** A replacement's
 *   output can therefore never become a source for a later pair — the cascade
 *   that turned `AB's Hillbilly Gyros` into `AB's Hillbilly UI Compass`.
 * - **Longest-first, non-overlapping.** Given `["Gyros", "Hillbilly Gyros"]`
 *   the longer term wins and the shorter never fires inside it.
 * - **No lookbehind anywhere.** This runs in the browser via
 *   `shared/composition-skin.js` and lookbehind is Safari 16.4+.
 * - **One boundary rule, promoted from `contamination-scan.js`**, so the
 *   scanner and the replacer cannot disagree about what a hit is.
 *
 * Pure, no imports, Worker-safe.
 */

/** Word characters for boundary purposes — Unicode letters, digits, marks. */
const WORD_CHAR_RE = /[\p{L}\p{N}\p{M}]/u;
const DIGIT_RE = /\d/;

function isWordChar(ch) {
  return ch !== undefined && WORD_CHAR_RE.test(ch);
}

function isAllDigits(value) {
  return /^\d+$/.test(value);
}

/** Digits only, for phone comparison. */
export function phoneDigits(value) {
  return String(value ?? '').replace(/\D+/g, '');
}

/**
 * Do two phone strings denote the same number? Tolerates a leading country
 * code of 1, which is why `+1 704-286-6985` and `(704) 286-6985` are one number.
 */
export function phoneDigitsMatch(a, b) {
  const x = phoneDigits(a);
  const y = phoneDigits(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const strip1 = (d) => (d.length === 11 && d.startsWith('1') ? d.slice(1) : d);
  return strip1(x) === strip1(y);
}

/**
 * A boundary-guarded pattern for one phone number, tolerant of ANY separator
 * arrangement.
 *
 * Enumerating string variants is unsound in principle and was unsound in fact:
 * `barbalandscapedesign`'s profile listed `310-877 4008` and `(310) 877-4008`,
 * and what shipped in the page was `(310) 877 4008` — a mixed form neither
 * covered. A pattern over the digits covers every arrangement at once.
 */
export function phoneTextPattern(value) {
  const digits = phoneDigits(value);
  if (digits.length < 7) return null;
  const core = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  const sep = '[\\s.\\-–—()]*';
  const body = core.split('').join(sep);
  return new RegExp(`(?:\\+?1${sep})?${sep}${body}`, 'gi');
}

/**
 * Find every occurrence of `term` in `haystack`.
 *
 * @param {string} haystack
 * @param {string} term
 * @param {{mode?: 'word'|'phone'|'exact', caseInsensitive?: boolean}} [opts]
 * @returns {Array<{start: number, end: number, matched: string}>}
 */
export function findOccurrences(haystack, term, opts = {}) {
  const text = String(haystack ?? '');
  const mode = opts.mode || 'word';
  if (!text) return [];

  if (mode === 'phone') {
    const pattern = phoneTextPattern(term);
    if (!pattern) return [];
    const out = [];
    for (const match of text.matchAll(pattern)) {
      const start = match.index;
      const end = start + match[0].length;
      // A phone must not sit inside a longer digit run: Shopify's
      // `template--22213612110044` contains a valid-looking 10-digit span.
      if (DIGIT_RE.test(text[start - 1] || '') || DIGIT_RE.test(text[end] || '')) continue;
      out.push({ start, end, matched: match[0] });
    }
    return out;
  }

  const needle = String(term ?? '');
  if (!needle) return [];
  const caseInsensitive = opts.caseInsensitive !== false;
  const hay = caseInsensitive ? text.toLowerCase() : text;
  const want = caseInsensitive ? needle.toLowerCase() : needle;
  const digitTerm = isAllDigits(want);

  const out = [];
  let index = hay.indexOf(want);
  while (index !== -1) {
    const end = index + want.length;
    let ok = true;
    if (mode === 'word') {
      if (digitTerm) {
        // Promoted verbatim from contamination-scan.js: a digit-only term must
        // sit on digit boundaries.
        ok = !DIGIT_RE.test(text[index - 1] || '') && !DIGIT_RE.test(text[end] || '');
      } else {
        // A word-mode term must not be flanked by word characters. This is what
        // stops "Gyros" firing inside "all gyros come with your choice" — and,
        // with no lookbehind, it works in Safari.
        const before = text[index - 1];
        const after = text[end];
        const startsWord = isWordChar(text[index]);
        const endsWord = isWordChar(text[end - 1]);
        ok = (!startsWord || !isWordChar(before)) && (!endsWord || !isWordChar(after));
      }
    }
    if (ok) out.push({ start: index, end, matched: text.slice(index, end) });
    index = hay.indexOf(want, index + Math.max(1, want.length));
  }
  return out;
}

/**
 * Replace many terms in ONE pass over the original string.
 *
 * @param {string} haystack
 * @param {Array<{from: string, to: string, mode?: string, caseInsensitive?: boolean}>} rules
 * @returns {{text: string, replaced: number}}
 */
export function applyOccurrences(haystack, rules) {
  const text = String(haystack ?? '');
  if (!text || !rules?.length) return { text, replaced: 0 };

  // Every span is located in the ORIGINAL text before anything is written, so
  // an earlier rule's output can never be seen by a later rule.
  const spans = [];
  for (const rule of rules) {
    for (const hit of findOccurrences(text, rule.from, {
      mode: rule.mode,
      caseInsensitive: rule.caseInsensitive
    })) {
      spans.push({ ...hit, to: rule.to ?? '' });
    }
  }
  if (!spans.length) return { text, replaced: 0 };

  // Longest first, then leftmost — so "Hillbilly Gyros" claims its span before
  // "Gyros" can, and a shorter term never fires inside a longer match.
  spans.sort((a, b) => b.end - b.start - (a.end - a.start) || a.start - b.start);
  const taken = [];
  const chosen = [];
  for (const span of spans) {
    if (taken.some((other) => span.start < other.end && other.start < span.end)) continue;
    taken.push(span);
    chosen.push(span);
  }
  chosen.sort((a, b) => a.start - b.start);

  let out = '';
  let cursor = 0;
  for (const span of chosen) {
    out += text.slice(cursor, span.start) + span.to;
    cursor = span.end;
  }
  out += text.slice(cursor);
  return { text: out, replaced: chosen.length };
}
